import { type ReactElement } from 'react';
import styles from './index-general-card.module.scss';
import { Font } from '@utmb/design-system/text/font';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconsEnum } from '@utmb/types/icons';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import { CloudinaryTransformEnum } from '@utmb/types/cloudinary-transform.enum';
import { Image } from '@utmb/design-system/image/Image';
import isMobile from '@utmb/utils/is-mobile';
import { VALID_INDEX_THRESHOLD } from '@utmb/utils/utmb-index.constants';
import { useTranslation } from 'next-i18next';

export interface IndexGeneralCardProps {
    value?: number;
    ranking?: number | null;
    isFrozen?: boolean;
    onDetailsClick?: () => void;
    variant?: 'myUtmb';
}

export function IndexGeneralCard({ value, ranking, isFrozen = false, onDetailsClick, variant }: IndexGeneralCardProps): ReactElement {
    const { t } = useTranslation();
    const isClickable = !isFrozen && !!value && !!onDetailsClick;
    const className =
        styles.indexGeneralCard +
        (isFrozen ? ` ${styles.frozen}` : '') +
        (isClickable ? ` ${styles.clickable}` : '') +
        (variant === 'myUtmb' ? ` ${styles.myUtmb}` : '');
    const Wrapper = isClickable ? 'button' : 'div';
    const wrapperProps = isClickable ? { type: 'button' as const, onClick: onDetailsClick } : {};
    return (
        <Wrapper className={className} {...wrapperProps}>
            {isFrozen && (
                <div className={styles.wrapExplanation} onClick={(e) => e.stopPropagation()}>
                    <Icon variant={IconsEnum.HELP} color={getColorStyle(ColorTypesEnum.WHITE)} width={20} height={20} />
                    <div className={styles.explanations}>
                        <Font.FuturaMedium desktop="14" mobile="14" color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}>
                            {t('singleRunner.statistics.frozenIndex.title')}
                        </Font.FuturaMedium>
                        <Font.FuturaBook desktop="14" mobile="14" color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}>
                            {t('singleRunner.statistics.frozenIndex.description')}
                        </Font.FuturaBook>
                    </div>
                </div>
            )}
            <div className={styles.logo}>
                <Image
                    objectFit="contain"
                    layout="fixed"
                    width={isMobile() ? 75 : 120}
                    height={30}
                    maxWidth={300}
                    image={pIdToCloudinary('/Common/categories/index_bg', 'UTMB Index Race')}
                    addOptions={[CloudinaryTransformEnum.TRIM]}
                />
            </div>
            {value ? (
                isFrozen ? (
                    <div className={styles.frozenContent}>
                        <div className={styles.frozenScoreRow}>
                            <Icon
                                clss={styles.frozenLogo}
                                variant={IconsEnum.FROZEN_INDEX}
                                color={getColorStyle(ColorTypesEnum.WHITE)}
                                width={isMobile() ? 40 : 50}
                                height={isMobile() ? 40 : 50}
                            />
                            <Font.OxaniumMedium className={styles.value} color={getColorStyle(ColorTypesEnum.WHITE)} desktop="32" mobile="28">
                                {value}
                            </Font.OxaniumMedium>
                        </div>
                        {ranking ? (
                            <Font.FuturaBook as="span" desktop="16" mobile="16">
                                {t('singleRunner.statistics.distribution.rank', { count: ranking, ordinal: true })}
                            </Font.FuturaBook>
                        ) : null}
                    </div>
                ) : (
                    <div className={styles.valueRow}>
                        {value > VALID_INDEX_THRESHOLD ? (
                            <Font.OxaniumMedium className={styles.value} color={getColorStyle(ColorTypesEnum.WHITE)} desktop="96" mobile="46">
                                {value}
                            </Font.OxaniumMedium>
                        ) : (
                            <Font.OxaniumMedium
                                className={styles.value + ' ' + styles.validValue}
                                color={getColorStyle(ColorTypesEnum.WHITE)}
                                desktop="30"
                                mobile="20"
                            >
                                {t('singleRunner.statistics.global.validIndex')}
                            </Font.OxaniumMedium>
                        )}
                    </div>
                )
            ) : (
                <div className={styles.lock}>
                    <Icon variant={IconsEnum.LOCK} color={getColorStyle(ColorTypesEnum.BLUE_LIGHT)} />
                </div>
            )}
            {(!isFrozen || !value) && (
                <div className={styles.footer}>
                    {value ? (
                        <>
                            <Font.FuturaMedium as="span" desktop="16" mobile="16">
                                {t('races.showMore')}
                            </Font.FuturaMedium>
                            <Icon variant={IconsEnum.CHEVRON_DOWN} />
                        </>
                    ) : (
                        <Font.FuturaMedium as="span" desktop="16" mobile="16">
                            {t('singleRunner.statistics.indexNoValid')}
                        </Font.FuturaMedium>
                    )}
                </div>
            )}
        </Wrapper>
    );
}
